import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UniversalSearchSheet } from './UniversalSearchSheet';

interface Ctx {
  isOpen: boolean;
  open: (initialQuery?: string) => void;
  close: () => void;
}

const UniversalSearchContext = createContext<Ctx | null>(null);

export function UniversalSearchProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [seed, setSeed] = useState('');

  const open = useCallback((initialQuery = '') => {
    setSeed(initialQuery);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <UniversalSearchContext.Provider value={value}>
      {children}
      <UniversalSearchSheet open={isOpen} onOpenChange={setIsOpen} initialQuery={seed} />
    </UniversalSearchContext.Provider>
  );
}

export function useUniversalSearch() {
  const ctx = useContext(UniversalSearchContext);
  if (!ctx) {
    // Graceful fallback for routes rendered outside the provider during tests.
    return {
      isOpen: false,
      open: () => { /* no-op */ },
      close: () => { /* no-op */ },
    };
  }
  return ctx;
}